<template>
  <section class="page">
    <h2 class="page__title">{{ $t('chpo.forces.title') }}</h2>

    <div class="page__table">
      <DataTable
        :columns="tColumns"
        :rows="tRows"
        :row-key="(r) => r.__key"
        :row-height="56"
      />
    </div>
    <div class="page__footer">
      <ViewNavButtons
        class="page__footer-nav"
        :prev-text="$t('nav.back')"
        :next-text="$t('nav.next')"
        :prev-to="{ path: '/chpo/map' }"
        :next-to="{ path: '/chpo/brief' }"
      />
    </div>
  </section>
</template>

<script>
import DataTable from '@/components/table/DataTable.vue'
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue'
import { checkOrCreateChpoByDate, computeChpoDate } from '@/api/chpo'

export default {
  name: 'ForcesView',
  components: { DataTable, ViewNavButtons },
  data() {
    return {
      // В примере: группы — строки, категории — колонки.
      groups: [
        { id: 'staff',        statsKey: 'in_house',   labelKey: 'chpo.forces.groupByStaff' },
        { id: 'list',         statsKey: 'total',      labelKey: 'chpo.forces.groupByList' },
        { id: 'expense',      statsKey: 'in_chpo',    labelKey: 'chpo.forces.groupExpense' },
        { id: 'helped',       statsKey: 'helped',     labelKey: 'chpo.forces.groupHelped' },
        { id: 'total_unit',   statsKey: 'total',      labelKey: 'chpo.forces.groupTotalUnit' },
        { id: 'total_used',   statsKey: 'total_used', labelKey: 'chpo.forces.groupTotalUsed' },
      ],
      fields: [
        { key: 'officers',   statsKey: 'oficers',          labelKey: 'chpo.forces.fOfficers' },
        { key: 'contract',   statsKey: 'contracts',        labelKey: 'chpo.forces.fContract' },
        { key: 'conscripts', statsKey: 'srochniki',        labelKey: 'chpo.forces.fConscripts' },
        { key: 'workers',    statsKey: 'workers',          labelKey: 'chpo.forces.fWorkers' },
        { key: 'dogs',       statsKey: 'dogs',             labelKey: 'chpo.forces.fDogs' },
        { key: 'horses',     statsKey: 'horses',           labelKey: 'chpo.forces.fHorses' },
        { key: 'vehicles',   statsKey: 'military_technic', labelKey: 'chpo.forces.fVehicles' },
        { key: 'radars',     statsKey: 'radio_stations',   labelKey: 'chpo.forces.fRadars' },
        { key: 'search',     statsKey: 'projectors',       labelKey: 'chpo.forces.fSearch' },
        { key: 'boats',      statsKey: 'boats',            labelKey: 'chpo.forces.fBoats' },
        { key: 'optical',    statsKey: 'devices',          labelKey: 'chpo.forces.fOptical' },
        { key: 'uav',        statsKey: 'drones',           labelKey: 'chpo.forces.fUav' },
      ],

      // values[field.key][group.statsKey] = number
      values: {},
      bookContext: null,
    }
  },
  computed: {
    tColumns() {
      const cols = [{ key: '__group', label: this.$t('chpo.forces.colGroup'), width: 220 }]
      this.fields.forEach(f => cols.push({ key: f.key, label: this.$t(f.labelKey), width: 180 }))
      return cols
    },
    tRows() {
      return this.groups.map(g => {
        const row = { __key: g.id, __group: this.$t(g.labelKey) }
        this.fields.forEach(f => {
          const v = this.values?.[f.key]?.[g.statsKey]
          row[f.key] = (v ?? v === 0) ? v : 0
        })
        return row
      })
    },
  },
  async mounted() {
    try {
      const ctx = await checkOrCreateChpoByDate(computeChpoDate())
      this.bookContext = ctx || {}
      const stats = this.bookContext?.stats || {}

      const values = {}
      this.fields.forEach(field => {
        const statKey = field.statsKey || field.key
        const statObj = stats[statKey] || {}
        const rowVals = {}
        this.groups.forEach(g => {
          // Backward compatibility: older payloads may use `in_book` instead of `in_chpo`.
          const lookupKey = (g.statsKey === 'in_chpo' && statObj.in_chpo == null) ? 'in_book' : g.statsKey
          const val = statObj[lookupKey]
          rowVals[g.statsKey] = (val ?? val === 0) ? val : 0
        })
        values[field.key] = rowVals
      })
      this.values = values
    } catch (e) {
      console.error('ForcesView: failed to load stats via API', e)
      this.values = {}
    }
  }
}
</script>

<style scoped>
.page {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cell-text { text-align: center; width: 100%; }

.page__title {
  margin: 0;
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  line-height: 33.6px;
}

.page__table { width: 100%; }
.page__pagination { display: flex; }

.page__footer {
  display: flex;
  justify-content: flex-end;
}


.page__table :deep(.tbl__row .tbl__cell:not(:first-child)) {
  justify-content: center;
}
.page__table :deep(.tbl__row .tbl__cell:not(:first-child) .tbl__text) {
  text-align: center;
}
</style>
